import React, { useState } from 'react';
import { Button, Checkbox, Divider, Modal, Space, Table, Tag, Tooltip, Upload, message } from 'antd';
import { DeleteOutlined, DownloadOutlined, FileAddOutlined, InboxOutlined } from '@ant-design/icons';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';
import { getAttachmentUrl } from '../../utils/attachmentUrl';
import { downloadFileAsBlob } from '../../utils/downloadFile';
import { describeUploadError, ensureUploadSize } from '../../utils/uploadLimits';
import {
  addNonStdFinalAttachmentsFromEntries,
  deleteNonStdFinalAttachment,
  uploadNonStdFinalAttachment,
} from '../../services/nonstandardOps';
import { NonStdEntry, NonStdFinalAttachment } from '../../types/nonstandardOps';
import MediaPreviewModal, { MediaPreviewTarget, previewKindOf } from './MediaPreviewModal';

interface Props {
  instructionId: number;
  attachments: NonStdFinalAttachment[];
  /** 全部执行记录：从里面挑附件加入终审结果 */
  entries: NonStdEntry[];
  canEdit: boolean;
  onChanged: () => void;
}

const errorText = (err: any, fallback: string) => {
  const data = err?.response?.data;
  // 没有服务器响应（网络中断/超时）或被入口拒绝（413）时，给出明确的原因
  if (!err?.response || err.response.status === 413) return describeUploadError(err);
  return data?.detail || (data && typeof data === 'object' ? Object.values(data).flat().join('；') : '') || err?.message || fallback;
};

const formatSize = (size?: number | null) => {
  if (!size) return '-';
  if (size >= 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(size / 1024))} KB`;
};

/**
 * 终审附件：管理员选定后，客户在指令详情的"执行结果"里看到。
 * 布局和常规指令的终审附件一致：拖拽上传区 + 附件表格（可勾选批量下载/批量删除）；
 * 另外保留这类指令特有的入口：从执行记录的附件里选择。执行记录本身保存在指令详情里，不再生成执行记录报告（自动挂给客户会把内部记录一起带出去）。
 */
const FinalAttachmentsPanel: React.FC<Props> = ({ instructionId, attachments, entries, canEdit, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [pickOpen, setPickOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<number[]>([]);
  const [previewTarget, setPreviewTarget] = useState<MediaPreviewTarget | null>(null);

  const candidates = entries.flatMap(entry => (entry.attachments || []).map(att => ({
    key: `${entry.id}|${att.url}`, entryId: entry.id, url: att.url, name: att.name || att.url.split('/').pop() || '附件', round: entry.round_no, kind: entry.kind,
  })));

  const run = async (action: () => Promise<unknown>, okText: string) => {
    setBusy(true);
    try {
      await action();
      message.success(okText);
      onChanged();
    } catch (err: any) {
      message.error(errorText(err, '操作失败'));
    } finally {
      setBusy(false);
    }
  };

  const selected = attachments.filter(att => selectedKeys.includes(att.id));

  const download = async (att: NonStdFinalAttachment) => {
    try {
      await downloadFileAsBlob(getAttachmentUrl({ url: att.url }), att.name);
    } catch {
      message.error(`下载失败：${att.name}`);
    }
  };

  const batchDownload = async () => {
    for (const att of selected) {
      // 逐个下载，避免浏览器把连续的下载当成弹窗拦掉
      await download(att);
    }
  };

  const batchDelete = () => {
    Modal.confirm({
      title: `删除选中的 ${selected.length} 个终审附件？`,
      content: '删除后客户将看不到这些文件。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        await run(async () => {
          for (const att of selected) {
            await deleteNonStdFinalAttachment(instructionId, att.id);
          }
          setSelectedKeys([]);
        }, `已删除 ${selected.length} 个附件`);
      },
    });
  };

  return (
    <div>
      {canEdit ? (
        <>
          <Upload.Dragger
            multiple
            showUploadList={false}
            disabled={busy}
            style={{ padding: 12 }}
            beforeUpload={(file) => {
              if (ensureUploadSize(file)) {
                void run(() => uploadNonStdFinalAttachment(instructionId, file as File), `已上传：${file.name}`);
              }
              return false;
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">点击或拖拽上传终审附件</p>
            <p className="ant-upload-hint">这些附件会作为指令执行结果提供给客户；不会修改执行数据。</p>
          </Upload.Dragger>
          <Space wrap style={{ marginTop: 12 }}>
            <Button icon={<FileAddOutlined />} disabled={busy || candidates.length === 0} onClick={() => { setPicked([]); setPickOpen(true); }}>
              从执行记录的附件里选择
            </Button>
          </Space>
          <Divider style={{ margin: '12px 0' }} />
        </>
      ) : null}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <Space>
          <Button size="small" icon={<DownloadOutlined />} disabled={selected.length === 0} onClick={() => { void batchDownload(); }}>批量下载</Button>
          {canEdit ? (
            <Button size="small" danger icon={<DeleteOutlined />} disabled={selected.length === 0 || busy} onClick={batchDelete}>批量删除</Button>
          ) : null}
        </Space>
      </div>
      <Table
        size="small"
        rowKey="id"
        dataSource={attachments}
        tableLayout="fixed"
        locale={{ emptyText: '还没有终审附件' }}
        rowSelection={{ selectedRowKeys: selectedKeys, onChange: keys => setSelectedKeys(keys as number[]) }}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
        }}
        columns={[
          {
            title: '文件名',
            dataIndex: 'name',
            ellipsis: true,
            render: (_: any, att: NonStdFinalAttachment) => (
              <Space size={6}>
                {previewKindOf(att.name) || previewKindOf(att.url)
                  ? <a onClick={() => setPreviewTarget({ url: getAttachmentUrl({ url: att.url }), name: att.name })}>{att.name}</a>
                  : <a href={getAttachmentUrl({ url: att.url })} target="_blank" rel="noopener noreferrer">{att.name}</a>}
              </Space>
            ),
          },
          { title: '大小', dataIndex: 'size', width: 90, render: (v: number | null) => formatSize(v) },
          {
            title: '上传时间',
            dataIndex: 'created_at',
            width: 150,
            render: (v: string) => (v ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm" showTimezone={false} /> : '-'),
          },
          {
            title: '操作',
            width: 150,
            render: (_: any, att: NonStdFinalAttachment) => (
              <Space size={8}>
                {/* 在线预览：图片、视频、PDF、Excel/CSV 表格可以直接在弹窗里看；Word 等其他格式不支持，请下载 */}
                {previewKindOf(att.name) || previewKindOf(att.url)
                  ? <a onClick={() => setPreviewTarget({ url: getAttachmentUrl({ url: att.url }), name: att.name })}>预览</a>
                  : <Tooltip title="该格式暂不支持在线预览，请下载后查看"><span style={{ color: '#bfbfbf', cursor: 'not-allowed' }}>预览</span></Tooltip>}
                <a onClick={() => { void download(att); }}>下载</a>
                {canEdit ? <a style={{ color: '#cf1322' }} onClick={() => run(() => deleteNonStdFinalAttachment(instructionId, att.id), '已删除')}>删除</a> : null}
              </Space>
            ),
          },
        ]}
      />
      <MediaPreviewModal target={previewTarget} onClose={() => setPreviewTarget(null)} />
      <Modal
        title="选择要加入终审结果的附件"
        open={pickOpen}
        onCancel={() => setPickOpen(false)}
        okText="加入终审结果"
        cancelText="取消"
        okButtonProps={{ disabled: picked.length === 0, loading: busy }}
        onOk={async () => {
          const picks = candidates.filter(c => picked.includes(c.key)).map(c => ({ entry_id: c.entryId, url: c.url }));
          await run(() => addNonStdFinalAttachmentsFromEntries(instructionId, picks), `已加入 ${picks.length} 个附件`);
          setPickOpen(false);
        }}
        destroyOnClose
        width={640}
      >
        <Checkbox.Group style={{ width: '100%' }} value={picked} onChange={values => setPicked(values as string[])}>
          {candidates.map(c => (
            <div key={c.key} style={{ padding: '6px 0', borderBottom: '1px solid #f5f5f5' }}>
              <Checkbox value={c.key}>
                <Tag>第 {c.round} 轮</Tag>
                {c.name}
              </Checkbox>
            </div>
          ))}
        </Checkbox.Group>
      </Modal>
    </div>
  );
};

export default FinalAttachmentsPanel;
